/**
 * Submit button should be isolated from other form elements based on UI design
 * It's might needed to be aligned left/center/right, but still in it's own row
 *
 * @example
 * <SubmitRow lobState={carLobState} />
 */
import * as React from "react";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { OffsetGridItem } from "components/shared/OffsetGridItem/OffsetGridItem";
import { LOBState } from "src/stores/wizard/state";
import { SubmitButton } from "src/components/shared/SubmitButton/SubmitButton";
import { getShoppingGlobalMicroMessage } from "components/shared/StorefrontWizard/SubmitRow/UISShoppingGlobal";
import { observer } from "mobx-react";

export interface SubmitRowProps {
  lobState: LOBState;
  className?: string;
  rfrr?: string;
}

export const SubmitRow: React.FunctionComponent<SubmitRowProps> = observer(({ lobState, className, rfrr }) => {
  const { formatText } = useLocalization();
  const submitGridcolumns: UitkLayoutConditionalGridColumnRow = { small: 1, medium: 8 };
  const offsetColumn: UitkLayoutConditionalGridSpan = { medium: 3 };
  const submitColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 2 };

  const defaultClassName = `all-b-margin-two all-t-margin-six`;

  const wizardRegionContext = React.useContext(StorefrontWizardRegionTypesContext);
  const isStorefront =
    StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_BEX === wizardRegionContext ||
    StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_COMET === wizardRegionContext;
  const wizardSearchButtonSubTextExperimentName = "PWA_WizardSearchButtonSubText";
  const isPackage = lobState.moduleName === "wizard-package-pwa";
  const defaultPackageSubLOB = "fh";
  const subTextLocKey = lobState.config.form.submitButton.subTextTokens?.[lobState.subLOBState || defaultPackageSubLOB];
  const subText = formatText(subTextLocKey);

  const renderSubmitButton = (submitSubText?: string) => (
    <SubmitButton
      lobState={lobState}
      colSpan={submitColSpan}
      rfrr={rfrr}
      uisPrimeMicroMessages={uisShoppingGlobal}
      subText={submitSubText}
    />
  );

  const uisShoppingGlobal = getShoppingGlobalMicroMessage(lobState);

  const fixedWidthSubmitGrid = (
    <UitkLayoutGrid columns={submitGridcolumns} space="three" className={`${defaultClassName} ${className}`}>
      {isStorefront && <OffsetGridItem colSpan={offsetColumn} />}

      {renderSubmitButton()}
    </UitkLayoutGrid>
  );

  const flexibleWidthSubmitGrid = (
    <UitkLayoutFlex justifyContent="center" className={`${defaultClassName} ${className}`}>
      <Viewport>
        <ViewSmall>
          <UitkLayoutFlexItem minWidth="full_width">
            <div>{renderSubmitButton(subText)}</div>
          </UitkLayoutFlexItem>
        </ViewSmall>

        <ViewMedium>
          <UitkLayoutFlexItem>
            <div>{renderSubmitButton(subText)}</div>
          </UitkLayoutFlexItem>
        </ViewMedium>
      </Viewport>
    </UitkLayoutFlex>
  );

  return (
    <Experiment name={wizardSearchButtonSubTextExperimentName}>
      <ExperimentControl>{fixedWidthSubmitGrid}</ExperimentControl>

      <ExperimentVariant bucket={1}>
        {!isPackage && fixedWidthSubmitGrid}

        {isPackage && flexibleWidthSubmitGrid}
      </ExperimentVariant>
    </Experiment>
  );
});
